import { useState, useEffect, useRef } from "react";
import { API_BASE_URL } from "../config";
import "../assets/css/flashcardgame.css";
import "../assets/css/vocabtest.css";
import { fillPlaceholdersForCard, loadLanguagePlaceholderMap, resolveCardDisplayText, scanCardPlaceholders } from "../utils/vocabGameHelpers";
import { speakText, stopSpeech, PRONUNCIATION_PASS_SCORE } from "../utils/audioHelpers";
import { navigateToDashboard, getEffectiveLangId } from "../utils/navigationHelpers";
import { authFetch } from "../utils/auth";
import LoadingScreen from "../components/common/LoadingScreen";
import SpeakerButton from "../components/common/SpeakerButton";
import PronunciationPractice from "../components/common/PronunciationPractice";
import InGameSettingsModal, { SettingsIcon } from "../components/common/InGameSettingsModal";
import BackToDashboardButton from "../components/common/BackToDashboardButton";
import SessionCompleteCard from "../components/common/SessionCompleteCard";
import "../assets/css/pronunciation.css";

const LANG_TAG = { 0: "de-DE", 1: "es-MX", 2: "fr-FR", 3: "ru-RU", 4: "pt-BR" };
const LANG_NAME = { 0: "German", 1: "Mexican Spanish", 2: "French", 3: "Russian", 4: "Brazilian Portuguese" };

function getLangTag(langId) { return LANG_TAG[parseInt(langId, 10)] ?? "de-DE"; }
function getLangName(langId) { return LANG_NAME[parseInt(langId, 10)] ?? "German"; }

function pronunciationWordClassName(word) {
    if (word.error_type === "Omission") {
        return "pronunciation-word pronunciation-word--omission";
    }
    if (word.accuracy_score >= 80) {
        return "pronunciation-word pronunciation-word--good";
    }
    if (word.accuracy_score >= 50) {
        return "pronunciation-word pronunciation-word--ok";
    }
    return "pronunciation-word pronunciation-word--poor";
}

function FeedbackAnswerBlock({ correctText, prompt, langTag, showForeignPrompt = false }) {
    return (
        <>
            <div className="test-feedback-answer-row">
                <span className="test-feedback-answer">Correct answer: {correctText}</span>
                {!showForeignPrompt && (
                    <SpeakerButton text={prompt} lang={langTag} label="Hear pronunciation" />
                )}
            </div>
            {showForeignPrompt && (
                <div className="test-feedback-answer-row test-feedback-answer-row--prompt">
                    <span className="test-feedback-prompt">{prompt}</span>
                    <SpeakerButton text={prompt} lang={langTag} label="Hear pronunciation" />
                </div>
            )}
        </>
    );
}

const MINIMUM_COMPLETION_SCORE = 0.8;

function removeContext(s) {
    return s.replace(/[([][^)\]]*[)\]]/g, "").trim();
}

function normalizeAnswer(s) {
    return removeContext(s).toLowerCase().replace(/[^a-z0-9\s]/g, "");
}

function formatChipWord(word) {
    return word.trim().toLowerCase();
}

function levenshtein(a, b) {
    const m = a.length, n = b.length;
    let prev = Array.from({ length: n + 1 }, (_, i) => i);
    for (let i = 1; i <= m; i++) {
        const cur = [i];
        for (let j = 1; j <= n; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
        }
        prev = cur;
    }
    return prev[n];
}

function shuffleArray(arr) {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
}

function pickDistractorWords(cards, currentIndex, correctWords) {
    const correctSet = new Set(correctWords);
    const others = cards.filter((_, i) => i !== currentIndex);
    const shuffled = shuffleArray(others);
    const distractors = [];
    for (const card of shuffled) {
        if (distractors.length >= 4) break;
        const { prompt } = resolveCardDisplayText(card);
        const words = prompt.split(/\s+/).filter(Boolean).map(formatChipWord);
        if (words.length === 0) continue;
        const word = words[Math.floor(Math.random() * words.length)];
        if (!correctSet.has(word) && !distractors.includes(word)) {
            distractors.push(word);
        }
    }
    return distractors;
}

function initRound(cards, index, placeholderMap, allowedTypes) {
    fillPlaceholdersForCard(cards[index], placeholderMap);
    const qType = allowedTypes[Math.floor(Math.random() * allowedTypes.length)] || 1;
    const round = { questionType: qType, typedAnswer: "", feedback: null, answerChips: [], bankChips: [] };
    if (qType === 2) {
        const { prompt } = resolveCardDisplayText(cards[index]);
        const cleanedPrompt = removeContext(prompt);
        const correctWords = cleanedPrompt.split(/\s+/).filter(Boolean).map(formatChipWord);
        const distractors = pickDistractorWords(cards, index, correctWords);
        round.bankChips = shuffleArray([...correctWords, ...distractors].map((word, i) => ({ id: `${index}-${i}-${word}`, word })));
    }
    return round;
}

function VocabTestGame() {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const languageId = getEffectiveLangId(urlSearchParams.get("lang_id"));
    const langTag = getLangTag(languageId);
    const langName = getLangName(languageId);
    const [cards, setCards] = useState([]);
    const [showSettings, setShowSettings] = useState(false);
    const [autoPlayAudio, setAutoPlayAudio] = useState(true);
    const [includeListening, setIncludeListening] = useState(true);
    const [includeSpeaking, setIncludeSpeaking] = useState(true);
    const [includeTranslation, setIncludeTranslation] = useState(true);

    const includeListeningRef = useRef(includeListening);
    const includeSpeakingRef = useRef(includeSpeaking);
    const includeTranslationRef = useRef(includeTranslation);

    useEffect(() => {
        includeListeningRef.current = includeListening;
    }, [includeListening]);

    useEffect(() => {
        includeSpeakingRef.current = includeSpeaking;
    }, [includeSpeaking]);

    useEffect(() => {
        includeTranslationRef.current = includeTranslation;
    }, [includeTranslation]);
    const [placeholderMap, setPlaceholderMap] = useState(null);
    const [error, setError] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [correctCount, setCorrectCount] = useState(0);
    const [questionType, setQuestionType] = useState(null);
    const [typedAnswer, setTypedAnswer] = useState("");
    const [answerChips, setAnswerChips] = useState([]);
    const [bankChips, setBankChips] = useState([]);
    const [feedback, setFeedback] = useState(null);
    const [pronunciationAttempt, setPronunciationAttempt] = useState(0);
    const [sessionDone, setSessionDone] = useState(false);
    const numPlaceholders = 5;
    const inputRef = useRef(null);
    const correctCountRef = useRef(0);
    const prevSpeakKeyRef = useRef("");

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const lessonId = urlParams.get("lesson_id");
        const languageId = urlParams.get("lang_id");
        if (lessonId) {
            fetch(`${API_BASE_URL}/api/vocab?tile_id=${lessonId}`)
                .then(res => res.json())
                .then(data => {
                    const initialCards = data.vocab.map(card => ({
                        ...card,
                        placeholders: []
                    }));

                    const shuffledCards = shuffleArray(initialCards);
                    const placeholders = scanCardPlaceholders(shuffledCards);

                    loadLanguagePlaceholderMap(languageId, placeholders, numPlaceholders)
                        .then(randomCodes => {
                            setPlaceholderMap(randomCodes);
                            if (shuffledCards.length > 0) {
                                fillPlaceholdersForCard(shuffledCards[0], randomCodes);
                            }
                            setCards(shuffledCards);
                        })
                        .catch(err => {
                            console.error("Failed to load placeholders for language:", languageId, err);
                            setPlaceholderMap({});
                            setCards(shuffledCards);
                        });
                })
                .catch((e) => {
                    setError(true);
                    console.log(e);
                });
        } else {
            setError(true);
        }
        return () => stopSpeech();
    }, []);

    useEffect(() => {
        if (cards.length === 0 || placeholderMap === null || sessionDone || currentIndex >= cards.length) return;

        const allowedTypes = [];
        if (includeTranslationRef.current) allowedTypes.push(1, 2);
        if (includeListeningRef.current) allowedTypes.push(3);
        if (includeSpeakingRef.current) allowedTypes.push(4);
        if (allowedTypes.length === 0) allowedTypes.push(1); // Fallback to type 1 if all turned off

        const round = initRound(cards, currentIndex, placeholderMap, allowedTypes);
        setQuestionType(round.questionType);
        setTypedAnswer(round.typedAnswer);
        setFeedback(round.feedback);
        setAnswerChips(round.answerChips);
        setBankChips(round.bankChips);
        setPronunciationAttempt(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentIndex, cards.length, placeholderMap, sessionDone]);

    useEffect(() => {
        if ((questionType === 1 || questionType === 3) && !feedback && inputRef.current) {
            inputRef.current.focus();
        }
    }, [currentIndex, questionType, feedback]);

    useEffect(() => {
        if (cards.length === 0 || placeholderMap === null || sessionDone || currentIndex >= cards.length || questionType === null) return;
        if (questionType !== 1 && questionType !== 3) return;

        // Only auto-play type 1 if autoPlayAudio is enabled.
        // Type 3 (listening) always plays because the user must hear the prompt to write the translation.
        if (questionType === 1 && !autoPlayAudio) return;

        const speakKey = `${currentIndex}-${questionType}`;
        if (speakKey === prevSpeakKeyRef.current) return;
        prevSpeakKeyRef.current = speakKey;
        const { prompt } = resolveCardDisplayText(cards[currentIndex]);
        const timeoutId = setTimeout(() => speakText(prompt, langTag), 300);
        return () => clearTimeout(timeoutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentIndex, questionType, cards.length, placeholderMap, sessionDone, langTag, autoPlayAudio]);

    function saveFamiliarity(correct, total) {
        const urlParams = new URLSearchParams(window.location.search);
        const tileId = urlParams.get("lesson_id");
        if (!tileId || total === 0) return;

        const sessionScore = correct / total;
        const score = Math.max(0, Math.min(1, sessionScore));

        authFetch(`${API_BASE_URL}/api/upsert-familiarity`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                tile_id: parseInt(tileId),
                familiarity_score: parseFloat(score.toFixed(3)),
                completed: score >= MINIMUM_COMPLETION_SCORE
            })
        }).catch(e => console.warn("Failed to save familiarity:", e));
    }

    function parseAlternativeAnswers(translation) {
        return translation
            .split('/')
            .map(alt => normalizeAnswer(alt.trim()))
            .filter(alt => alt.length > 0);
    }

    function userAnswerMatchesAny(userAnswer, translation) {
        const alternatives = parseAlternativeAnswers(translation);
        const normalized = normalizeAnswer(userAnswer);
        return alternatives.some(
            alt => normalized === alt || levenshtein(normalized, alt) <= 1
        );
    }

    function checkType1Answer(card) {
        const { translation } = resolveCardDisplayText(card);
        return userAnswerMatchesAny(typedAnswer, translation);
    }

    function checkType2Answer(card) {
        const { prompt } = resolveCardDisplayText(card);
        const expected = normalizeAnswer(prompt);
        const given = normalizeAnswer(answerChips.map(c => c.word).join(" "));
        return given === expected;
    }

    function handlePronunciationRedo() {
        if (feedback && feedback.isCorrect) {
            correctCountRef.current = Math.max(0, correctCountRef.current - 1);
            setCorrectCount(correctCountRef.current);
        }
        setFeedback(null);
        setPronunciationAttempt((prev) => prev + 1);
    }

    function handlePronunciationResult(result) {
        if (feedback) return;
        const { prompt } = resolveCardDisplayText(cards[currentIndex]);
        const isCorrect = result.overall_score >= PRONUNCIATION_PASS_SCORE;
        setFeedback({ isCorrect, correctText: prompt, pronunciationResult: result });
        if (isCorrect) {
            correctCountRef.current += 1;
            setCorrectCount(correctCountRef.current);
        }
    }

    function handleSubmit(e) {
        if (e) e.preventDefault();
        if (feedback) return;
        const card = cards[currentIndex];
        const { prompt, translation } = resolveCardDisplayText(card);

        const isCorrect = questionType === 2 ? checkType2Answer(card) : checkType1Answer(card);
        const correctText = removeContext(questionType === 2 ? prompt : translation);
        const foreignPrompt = questionType === 3 ? prompt : undefined;
        setFeedback({ isCorrect, correctText, foreignPrompt });
        if (isCorrect) {
            correctCountRef.current += 1;
            setCorrectCount(correctCountRef.current);
        }

        const currentScore = parseFloat(card.familiarity_score) || 0.0;
        const newScore = parseFloat(Math.max(0.0, Math.min(1.0, isCorrect ? currentScore + 0.3 : currentScore - 0.3)).toFixed(1));

        setCards(prevCards => {
            const nextCards = [...prevCards];
            nextCards[currentIndex] = {
                ...nextCards[currentIndex],
                familiarity_score: newScore
            };
            return nextCards;
        });

        // Fire-and-forget prompt familiarity upsert
        if (card.id != null) {
            authFetch(`${API_BASE_URL}/api/upsert-prompt-familiarity`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    prompt_id: card.id,
                    familiarity_score: newScore
                })
            }).catch(e => console.warn("Failed to save prompt familiarity:", e));
        }
    }

    function handleNext() {
        const nextIndex = currentIndex + 1;
        if (nextIndex >= cards.length) {
            saveFamiliarity(correctCountRef.current, cards.length);
            setSessionDone(true);
            return;
        }
        setPronunciationAttempt(0);
        setCurrentIndex(nextIndex);
    }

    function moveChipToAnswer(chip) {
        setBankChips(prev => prev.filter(c => c.id !== chip.id));
        setAnswerChips(prev => [...prev, chip]);
    }

    function moveChipToBank(chip) {
        setAnswerChips(prev => prev.filter(c => c.id !== chip.id));
        setBankChips(prev => [...prev, chip]);
    }

    function goToDashboard() {
        navigateToDashboard("testing");
    }

    if (error) {
        return <div><p className="error">Error loading vocabulary data.</p></div>;
    }

    const settingsMenu = (
        <InGameSettingsModal
            isOpen={showSettings}
            onClose={() => setShowSettings(false)}
            title="Settings"
        >
            <div className="in-game-settings-section">
                <label className="in-game-settings-checkbox-label">
                    <input
                        type="checkbox"
                        checked={autoPlayAudio}
                        onChange={e => setAutoPlayAudio(e.target.checked)}
                        className="in-game-settings-checkbox"
                    />
                    Automatically play foreign audio
                </label>
                <div className="in-game-settings-desc">
                    Automatically hear pronunciation when a card appears
                </div>
            </div>
            <div className="in-game-settings-section">
                <label className="in-game-settings-checkbox-label">
                    <input
                        type="checkbox"
                        checked={includeTranslation}
                        onChange={e => {
                            if (!e.target.checked && !includeListening && !includeSpeaking) return;
                            setIncludeTranslation(e.target.checked);
                        }}
                        className="in-game-settings-checkbox"
                    />
                    Include translation questions
                </label>
                <div className="in-game-settings-desc">
                    Include typed translation and word chip questions
                </div>
            </div>
            <div className="in-game-settings-section">
                <label className="in-game-settings-checkbox-label">
                    <input
                        type="checkbox"
                        checked={includeListening}
                        onChange={e => {
                            if (!e.target.checked && !includeTranslation && !includeSpeaking) return;
                            setIncludeListening(e.target.checked);
                        }}
                        className="in-game-settings-checkbox"
                    />
                    Include listening questions
                </label>
                <div className="in-game-settings-desc">
                    Include questions where you listen and type translation
                </div>
            </div>
            <div className="in-game-settings-section">
                <label className="in-game-settings-checkbox-label">
                    <input
                        type="checkbox"
                        checked={includeSpeaking}
                        onChange={e => {
                            if (!e.target.checked && !includeTranslation && !includeListening) return;
                            setIncludeSpeaking(e.target.checked);
                        }}
                        className="in-game-settings-checkbox"
                    />
                    Include speaking questions
                </label>
                <div className="in-game-settings-desc">
                    Include questions where you practice pronunciation
                </div>
            </div>
        </InGameSettingsModal>
    );

    if (cards.length === 0 || placeholderMap === null) {
        return (
            <LoadingScreen
                title="Loading vocabulary test..."
                onBack={goToDashboard}
                backLabel="Dashboard"
                onSettingsClick={() => setShowSettings(true)}
                settingsMenu={settingsMenu}
            />
        );
    }

    if (sessionDone) {
        const totalCount = cards.length;
        const percent = totalCount > 0 ? Math.round((correctCount / totalCount) * 100) : 0;
        return (
            <SessionCompleteCard
                title="Test Complete"
                stats={[
                    { label: "Correct answers", value: `${correctCount} / ${totalCount} (${percent}%)` }
                ]}
                onBack={goToDashboard}
            />
        );
    }

    const card = cards[currentIndex];
    const { prompt, translation } = resolveCardDisplayText(card);
    const totalCount = cards.length;

    return (
        <div className="vocab-test">
            <BackToDashboardButton floating onClick={goToDashboard} />
            <button
                type="button"
                aria-label="Settings"
                className="in-game-settings-btn"
                onClick={() => setShowSettings(true)}
            >
                <SettingsIcon size={28} />
            </button>
            {settingsMenu}
            <h1>Vocabulary Test</h1>
            <div className="vocab-test-progress">Question {currentIndex + 1} of {totalCount}</div>
            <div className="vocab-test-box">
                {questionType === 1 && (
                    <>
                        <div className="test-prompt-row">
                            <div className="test-prompt">{prompt}</div>
                            <SpeakerButton text={prompt} lang={langTag} label="Hear pronunciation" className="speaker-btn--card-front" />
                        </div>
                        <form onSubmit={handleSubmit}>
                            <input ref={inputRef} type="text" className="test-input" value={typedAnswer} onChange={e => setTypedAnswer(e.target.value)} disabled={!!feedback} autoComplete="off" />
                            {!feedback && <button type="submit" className="test-submit-btn">Submit</button>}
                        </form>
                    </>
                )}
                {questionType === 2 && (
                    <>
                        <div className="test-prompt">{translation}</div>
                        <div className="test-answer-area">
                            {answerChips.map(chip => (
                                <button key={chip.id} type="button" className="test-word-chip test-word-chip--selected" onClick={() => !feedback && moveChipToBank(chip)} disabled={!!feedback}>{chip.word}</button>
                            ))}
                        </div>
                        <div className="test-word-bank">
                            {bankChips.map(chip => (
                                <button key={chip.id} type="button" className="test-word-chip" onClick={() => !feedback && moveChipToAnswer(chip)} disabled={!!feedback}>{chip.word}</button>
                            ))}
                        </div>
                        {!feedback && <button type="button" className="test-submit-btn" onClick={handleSubmit} disabled={answerChips.length === 0}>Submit</button>}
                    </>
                )}
                {questionType === 3 && (
                    <>
                        <div className="test-listen-area">
                            <SpeakerButton text={prompt} lang={langTag} size={40} label="Play word" className="speaker-btn--listen" />
                            <div className="test-listen-hint">Tap to hear again</div>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <input ref={inputRef} type="text" className="test-input" value={typedAnswer} onChange={e => setTypedAnswer(e.target.value)} disabled={!!feedback} autoComplete="off" />
                            {!feedback && <button type="submit" className="test-submit-btn">Submit</button>}
                        </form>
                    </>
                )}
                {questionType === 4 && (
                    <>
                        <div className="test-prompt">{translation}</div>
                        <div className="test-listen-hint">Say this in {langName}:</div>
                        {!feedback && (
                            <div className="test-pronunciation-wrap">
                                <PronunciationPractice
                                    key={`${currentIndex}-${pronunciationAttempt}`}
                                    text={prompt}
                                    onResult={handlePronunciationResult}
                                    langCode={langTag}
                                />
                            </div>
                        )}
                        {feedback && (
                            <>
                                <div className={`test-feedback ${feedback.isCorrect ? "test-feedback--correct" : "test-feedback--wrong"}`}>
                                    {feedback.isCorrect ? "Correct" : "Incorrect"}
                                </div>
                                <FeedbackAnswerBlock correctText={feedback.correctText} prompt={prompt} langTag={langTag} />
                                {feedback.pronunciationResult && (
                                    <div className="test-pronunciation-feedback">
                                        <div className="pronunciation-overall-label">Overall pronunciation score</div>
                                        <div className="pronunciation-overall-score">
                                            {Math.round(feedback.pronunciationResult.overall_score)}
                                        </div>
                                        <div className="test-pronunciation-word-list">
                                            {feedback.pronunciationResult.words.map((word, index) => (
                                                <div key={`${word.word}-${index}`} className="test-pronunciation-word-item">
                                                    <span
                                                        className={pronunciationWordClassName(word)}
                                                        onClick={() => speakText(word.word, langTag)}
                                                        title="Click to hear pronunciation"
                                                    >
                                                        {word.word}
                                                    </span>
                                                    <span className="test-pronunciation-word-score">
                                                        {word.error_type === "Omission"
                                                            ? "Missed"
                                                            : `${Math.round(word.accuracy_score)}%`}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <div className="test-feedback-actions">
                                    <button type="button" className="test-redo-btn" onClick={handlePronunciationRedo}>
                                        Try again
                                    </button>
                                    <button type="button" className="test-submit-btn" onClick={handleNext}>Next</button>
                                </div>
                            </>
                        )}
                    </>
                )}
                {feedback && questionType !== 4 && (
                    <div>
                        <div className={`test-feedback ${feedback.isCorrect ? "test-feedback--correct" : "test-feedback--wrong"}`}>
                            {feedback.isCorrect ? "Correct" : "Incorrect"}
                        </div>
                        <FeedbackAnswerBlock
                            correctText={feedback.correctText}
                            prompt={prompt}
                            langTag={langTag}
                            showForeignPrompt={questionType === 3}
                        />
                        <button type="button" className="test-submit-btn" onClick={handleNext}>Next</button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default VocabTestGame;
